/**
 * Générateur — Utiliser la définition de la bissectrice pour constructions et problèmes
 * Attendu ID : 665 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés :
 *   Template 1 (calcul_bissectrice) : qcm_unique, texte, tableau_vf, texte_trou_drag, texte_trou_saisie
 *   Template 2 (construction_bissectrice) : programme_construction
 *
 * Labels de référence :
 *   - Construire la bissectrice d'un angle au rapporteur
 *   - Calculer la mesure du demi-angle quand la bissectrice est donnée
 *   - Calculer un angle en utilisant la propriété de la bissectrice
 *   - Écrire un programme de construction impliquant des bissectrices
 *
 * Variantes estimées : 300+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    makeProgrammeConstruction,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 665;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');
const ANGLES_CALCUL = [20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160];
const ANGLES_CONSTRUCTION = [30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150];
const BASE_ANGLES = [-20, -10, 0, 10, 20];

const VIEWBOX = { w: 600, h: 400 };
const PX_PER_CM = 30;
const COLOR_VERT = '#1D9E75';

const L_CALCUL = 'Calculer la mesure du demi-angle quand la bissectrice est donnée';
const L_CONSTRUCT = 'Construire la bissectrice d\'un angle au rapporteur';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_CALCUL,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

/** Encode une chaîne SVG en data URL (utf8 + encodeURIComponent). */
const svgToDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// ─── SVG Helper ──────────────────────────────────────────────
const svgAngle = (angleDeg, halfDeg, names, showMeasure) => {
    const [vName, xName, yName, tName] = names;
    const vx = 120, vy = 170, rayLen = 120;
    const toRad = (d) => d * Math.PI / 180;

    const x1 = vx + rayLen * Math.cos(toRad(0));
    const y1 = vy - rayLen * Math.sin(toRad(0));
    const x2 = vx + rayLen * Math.cos(toRad(angleDeg));
    const y2 = vy - rayLen * Math.sin(toRad(angleDeg));
    const tx = vx + rayLen * Math.cos(toRad(halfDeg));
    const ty = vy - rayLen * Math.sin(toRad(halfDeg));

    const arcR = 35;
    const a1x = vx + arcR;
    const a1y = vy;
    const a3x = vx + arcR * Math.cos(toRad(angleDeg));
    const a3y = vy - arcR * Math.sin(toRad(angleDeg));
    const largeArc = angleDeg > 180 ? 1 : 0;

    let svg = `<svg viewBox='0 0 300 200' xmlns='http://www.w3.org/2000/svg'>`;
    svg += `<line x1='${vx}' y1='${vy}' x2='${x1}' y2='${y1}' stroke='black' stroke-width='2'/>`;
    svg += `<line x1='${vx}' y1='${vy}' x2='${x2}' y2='${y2}' stroke='black' stroke-width='2'/>`;
    svg += `<line x1='${vx}' y1='${vy}' x2='${tx}' y2='${ty}' stroke='${COLOR_VERT}' stroke-width='2' stroke-dasharray='6,3'/>`;
    svg += `<text x='${vx - 15}' y='${vy + 15}' font-size='14'>${vName}</text>`;
    svg += `<text x='${x1 + 5}' y='${y1 + 5}' font-size='12' font-style='italic'>${xName}</text>`;
    svg += `<text x='${x2 + 5}' y='${y2 - 5}' font-size='12' font-style='italic'>${yName}</text>`;
    svg += `<text x='${tx + 5}' y='${ty}' font-size='12' fill='${COLOR_VERT}' font-style='italic'>${tName}</text>`;
    if (showMeasure) {
        svg += `<path d='M ${a1x} ${a1y} A ${arcR} ${arcR} 0 ${largeArc} 0 ${a3x} ${a3y}' fill='none' stroke='grey' stroke-width='1'/>`;
        svg += `<text x='${vx + arcR + 8}' y='${vy - 10}' font-size='10' fill='grey'>${angleDeg}°</text>`;
    }
    svg += `</svg>`;
    return svg;
};

// ─── Template 1 : calcul_bissectrice ─────────────────────────

// QCM — Trois variantes : directe, inverse, config supplémentaire
const genCalculQcm = () => {
    const letters = pickLetters(5);
    const [V, X, Y, T, Z] = letters;
    const variante = randomChoice(['directe', 'inverse', 'config']);

    let texte, bonne, pieges;
    let svgAngleDeg = 0, svgHalfDeg = 0;

    if (variante === 'directe') {
        const angle = randomChoice(ANGLES_CALCUL);
        const half = angle / 2;
        texte = `<p>[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$ qui mesure ${angle}°. Quelle est la mesure de l'angle $\\widehat{${X}${V}${T}}$ ?</p>{{element:0}}`;
        bonne = `${half}°`;
        svgAngleDeg = angle;
        svgHalfDeg = half;
        // Generate unique pièges only
        const candidats = new Set();
        candidats.add(bonne);
        const allPieges = [`${angle}°`, `${angle * 2}°`, `${90 - half}°`, `${180 - angle}°`, `${half + 10}°`, `${half - 10}°`];
        pieges = allPieges.filter(p => { if (candidats.has(p) || parseInt(p) <= 0) return false; candidats.add(p); return true; }).slice(0, 3);
    } else if (variante === 'inverse') {
        const half = randomChoice([15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75]);
        const angle = half * 2;
        texte = `<p>[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$. L'angle $\\widehat{${X}${V}${T}}$ mesure ${half}°. Quelle est la mesure de l'angle $\\widehat{${X}${V}${Y}}$ ?</p>{{element:0}}`;
        bonne = `${angle}°`;
        svgAngleDeg = angle;
        svgHalfDeg = half;
        const candidats = new Set();
        candidats.add(bonne);
        const allPieges = [`${half}°`, `${Math.round(half / 2)}°`, `${180 - half}°`, `${angle + half}°`];
        pieges = allPieges.filter(p => { if (candidats.has(p) || parseInt(p) <= 0) return false; candidats.add(p); return true; }).slice(0, 3);
    } else {
        // Config supplémentaire : angle plat
        const angle1 = randomChoice([100, 110, 120, 130, 140, 150, 160]);
        const angle2 = 180 - angle1;
        const half2 = angle2 / 2;
        texte = `<p>Les angles $\\widehat{${X}${V}${Y}}$ et $\\widehat{${Y}${V}${Z}}$ forment un angle plat. L'angle $\\widehat{${X}${V}${Y}}$ mesure ${angle1}°. [${V}${T}) est la bissectrice de l'angle $\\widehat{${Y}${V}${Z}}$. Quelle est la mesure de l'angle $\\widehat{${Y}${V}${T}}$ ?</p>`;
        bonne = `${half2}°`;
        const candidats = new Set();
        candidats.add(bonne);
        const allPieges = [`${angle2}°`, `${angle1 / 2}°`, `90°`, `${angle1}°`];
        pieges = allPieges.filter(p => { if (candidats.has(p)) return false; candidats.add(p); return true; }).slice(0, 3);
    }

    // SVG: directe shows full angle measure (given info); inverse shows geometry only
    // (showing the full angle on inverse would give away the answer)
    let svg = null;
    if (variante === 'directe') {
        svg = svgAngle(svgAngleDeg, svgHalfDeg, [V, X, Y, T], true);
    } else if (variante === 'inverse') {
        svg = svgAngle(svgAngleDeg, svgHalfDeg, [V, X, Y, T], false);
    }

    // makeQcm attend le CONTENU de la bonne réponse (mélange + lettre gérés par lui)
    const payload = makeQcm([bonne, ...pieges], bonne, texte, opts(L_CALCUL));
    if (svg) {
        payload.elements_additionnels = {
            version: 1,
            elements: [{ type: 'image', config: { imageData: svgToDataUrl(svg) } }],
        };
    }
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: variante };
    payload._meta.generatorKey = 'calcul_bissectrice_qcm_unique';
    return payload;
};

// Saisie — Directe ou inverse
const genCalculSaisie = () => {
    const [V, X, Y, T] = pickLetters(4);
    const sens = randomChoice(['directe', 'inverse']);

    let texte, bonne;
    if (sens === 'directe') {
        const angle = randomChoice(ANGLES_CALCUL);
        const half = angle / 2;
        texte = `<p>[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$ qui mesure ${angle}°. Quelle est la mesure, en degrés, de l'angle $\\widehat{${X}${V}${T}}$ ?</p>{{element:0}}`;
        bonne = String(half);

        const svg = svgAngle(angle, half, [V, X, Y, T], true);
        const payload = makeSaisie(bonne, texte, { ...opts(L_CALCUL), strict: true });
        payload.elements_additionnels = {
            version: 1,
            elements: [{ type: 'image', config: { imageData: svgToDataUrl(svg) } }],
        };
        payload._meta.generatorKey = 'calcul_bissectrice_texte';
        return payload;
    } else {
        const half = randomChoice([15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75]);
        const angle = half * 2;
        texte = `<p>[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$. L'angle $\\widehat{${X}${V}${T}}$ mesure ${half}°. Quelle est la mesure, en degrés, de l'angle $\\widehat{${X}${V}${Y}}$ ?</p>`;
        bonne = String(angle);

        const payload = makeSaisie(bonne, texte, { ...opts(L_CALCUL), strict: true });
        payload._meta.generatorKey = 'calcul_bissectrice_texte';
        return payload;
    }
};

// Tableau V/F
const genCalculVF = () => {
    const [V, X, Y, T] = pickLetters(4);
    const angle = randomChoice(ANGLES_CALCUL);
    const half = angle / 2;

    const vrais = shuffle([
        `L'angle $\\widehat{${X}${V}${T}}$ mesure ${half}°.`,
        `L'angle $\\widehat{${T}${V}${Y}}$ mesure ${half}°.`,
        `Les angles $\\widehat{${X}${V}${T}}$ et $\\widehat{${T}${V}${Y}}$ sont adjacents et de même mesure.`,
        // ⚠️ pas de '[' brut en ligne de tableau V/F (casse parseComplexAnswer) → $\lbrack$
        `$\\lbrack ${V}${T})$ est un axe de symétrie de l'angle $\\widehat{${X}${V}${Y}}$.`,
    ]);

    // Build faux pool, filtering out any value that equals a vrai value
    const fauxCandidates = [
        { text: `L'angle $\\widehat{${X}${V}${T}}$ mesure ${angle}°.`, val: angle },
        { text: `L'angle $\\widehat{${T}${V}${Y}}$ mesure ${180 - half}°.`, val: 180 - half },
        { text: `L'angle $\\widehat{${X}${V}${T}}$ mesure ${90 - half}°.`, val: 90 - half },
        { text: `L'angle $\\widehat{${X}${V}${T}}$ mesure ${angle * 2}°.`, val: angle * 2 },
        { text: `$\\lbrack ${V}${X})$ est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$.`, val: -1 },
        { text: `La somme des angles $\\widehat{${X}${V}${T}}$ et $\\widehat{${T}${V}${Y}}$ vaut ${angle * 2}°.`, val: angle * 2 },
    ];
    // Remove faux whose numeric text would collide with a vrai line
    const vraiTexts = new Set(vrais.map(v => v));
    const faux = shuffle(fauxCandidates.filter(f => !vraiTexts.has(f.text)).map(f => f.text));

    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;
    const selectedVrais = vrais.slice(0, nbVrais);

    // Filter faux against selected vrais to avoid text collisions
    const selectedVraisSet = new Set(selectedVrais);
    const filteredFaux = faux.filter(f => !selectedVraisSet.has(f));
    const selectedFaux = filteredFaux.slice(0, nbFaux);

    // Final dedup safety net
    const allSelected = [...selectedVrais, ...selectedFaux];
    const uniqueSelected = [...new Set(allSelected)];
    if (uniqueSelected.length < allSelected.length) {
        // Collision detected — regenerate by calling ourselves
        return genCalculVF();
    }

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);

    const lignes = mixed.map(m => m.l);
    const bonnesReponses = mixed.map(m => m.r);

    const texte = `<p>[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$ qui mesure ${angle}°. Indique si chaque affirmation est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_CALCUL));
    payload._meta.generatorKey = 'calcul_bissectrice_tableau_vf';
    return payload;
};

// Texte à trous (drag)
const genCalculTrouDrag = () => {
    const [V, X, Y, T, Z] = pickLetters(5);
    const modele = randomChoice(['direct', 'config']);

    if (modele === 'direct') {
        const angle = randomChoice(ANGLES_CALCUL);
        const half = angle / 2;
        const phrase = `L'angle $\\widehat{${X}${V}${Y}}$ mesure ${angle}°. [${V}${T}) est sa bissectrice. La bissectrice partage l'angle en deux angles {{égaux}}. L'angle $\\widehat{${X}${V}${T}}$ mesure donc {{${half}}}° et l'angle $\\widehat{${T}${V}${Y}}$ mesure aussi {{${half}}}°.`;
        // Distracteurs filtrés contre les mots attendus (un distracteur égal à
        // un bon mot = deux bonnes réponses au scoring)
        const mots = ['égaux', `${half}`];
        const distracteurs = [...new Set([`${angle}`, `${angle * 2}`, `${90 - half}`, 'supplémentaires'])]
            .filter(d => !mots.includes(d));
        const texte = `<p>Complète le raisonnement en glissant les valeurs proposées.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALCUL), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'calcul_bissectrice_texte_trou_drag';
        return payload;
    } else {
        const angle1 = randomChoice([100, 110, 120, 130, 140, 150, 160]);
        const angle2 = 180 - angle1;
        const half2 = angle2 / 2;
        const phrase = `L'angle $\\widehat{${X}${V}${Y}}$ et l'angle $\\widehat{${Y}${V}${Z}}$ forment un angle plat. L'angle $\\widehat{${X}${V}${Y}}$ mesure ${angle1}°. Donc l'angle $\\widehat{${Y}${V}${Z}}$ mesure {{${angle2}}}°. [${V}${T}) est la bissectrice de l'angle $\\widehat{${Y}${V}${Z}}$. L'angle $\\widehat{${Y}${V}${T}}$ mesure {{${half2}}}°.`;
        // Distracteurs filtrés contre les mots attendus (ex. angle1=120 →
        // angle1/2 = 60 = angle2 → collision)
        const mots = [`${angle2}`, `${half2}`];
        const distracteurs = [...new Set([`${angle1}`, `${angle1 / 2}`, '180', '90'])]
            .filter(d => !mots.includes(d));
        const texte = `<p>Complète le raisonnement en glissant les valeurs proposées.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALCUL), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'calcul_bissectrice_texte_trou_drag';
        return payload;
    }
};

// Texte à trous (saisie)
const genCalculTrouSaisie = () => {
    const [V, X, Y, T] = pickLetters(4);
    const modele = randomChoice(['direct', 'inverse']);

    if (modele === 'direct') {
        const angle = randomChoice(ANGLES_CALCUL);
        const half = angle / 2;
        const phrase = `L'angle $\\widehat{${X}${V}${Y}}$ mesure ${angle}°. [${V}${T}) est la bissectrice de cet angle. L'angle $\\widehat{${X}${V}${T}}$ mesure {{${half}}}° et l'angle $\\widehat{${T}${V}${Y}}$ mesure {{${half}}}°.`;
        const texte = `<p>Complète avec les bonnes mesures d'angles (en degrés).</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALCUL), mode: 'saisie' });
        payload._meta.generatorKey = 'calcul_bissectrice_texte_trou_saisie';
        return payload;
    } else {
        const half = randomChoice([15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75]);
        const angle = half * 2;
        const phrase = `[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$. L'angle $\\widehat{${X}${V}${T}}$ mesure ${half}°. Donc l'angle $\\widehat{${X}${V}${Y}}$ mesure {{${angle}}}°.`;
        const texte = `<p>Complète avec la bonne mesure d'angle (en degrés).</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALCUL), mode: 'saisie' });
        payload._meta.generatorKey = 'calcul_bissectrice_texte_trou_saisie';
        return payload;
    }
};

// ─── Template 2 : construction_bissectrice ───────────────────

const genConstruction = () => {
    const [A_name, X_name, Y_name, T_name] = pickLetters(4);
    const targetAngle = randomChoice(ANGLES_CONSTRUCTION);
    const baseAngle = randomChoice(BASE_ANGLES);
    const halfAngle = targetAngle / 2;
    const bissectriceAngle = baseAngle + halfAngle;

    // Vertex position with jitter
    const vx = 180 + randInt(-30, 30);
    const vy = 260 + randInt(-20, 20);

    const points = [{ name: A_name, x: vx, y: vy }];

    const rayLen = randInt(180, 240);

    const preShapes = [
        { type: 'ray', from: A_name, angleDeg: baseAngle, length: rayLen, labelEnd: X_name },
        { type: 'ray', from: A_name, angleDeg: baseAngle + targetAngle, length: rayLen, labelEnd: Y_name },
    ];

    const tasks = [
        {
            id: 'bissectrice',
            kind: 'angle_ray',
            consigne: `Trace en <b>vert</b> la bissectrice [${A_name}${T_name}) de l'angle ${X_name}${A_name}${Y_name}.`,
            color: COLOR_VERT,
            tolerance_deg: 2,
            shape: { type: 'ray', from: A_name, angleDeg: bissectriceAngle },
        },
    ];

    // Pas de rappel de la méthode dans l'énoncé : il la donnait en clair, et son
    // incise « depuis le côté [AX) » est fausse depuis que les deux côtés de
    // l'angle sont acceptés comme base. La méthode est passée au coup de pouce.
    const texte = `<p>Utilise le rapporteur pour tracer la bissectrice [${A_name}${T_name}) de l'angle ${X_name}${A_name}${Y_name} ci-dessous.</p>`;

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: 12,
            viewBox: VIEWBOX,
            points,
            preShapes,
            tasks,
        },
        texte,
        { ...opts(L_CONSTRUCT), label: L_CONSTRUCT },
    );
    payload._meta.generatorKey = 'construction_bissectrice_programme_construction';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────
const GENERATORS = {
    calcul_bissectrice_qcm_unique: genCalculQcm,
    calcul_bissectrice_texte: genCalculSaisie,
    calcul_bissectrice_tableau_vf: genCalculVF,
    calcul_bissectrice_texte_trou_drag: genCalculTrouDrag,
    calcul_bissectrice_texte_trou_saisie: genCalculTrouSaisie,
    construction_bissectrice_programme_construction: genConstruction,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
    programme_construction: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'calcul_bissectrice',
        label: 'Calcul d\'angles avec la bissectrice',
        formatsList: buildFormats('calcul_bissectrice'),
    },
    {
        id: 'construction_bissectrice',
        label: 'Construction de la bissectrice',
        formatsList: buildFormats('construction_bissectrice'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du REGISTRE fait foi, toujours : c'est elle qui est persistée dans
    // `generator_key` et qui identifie le format. Un `if (!...)` laissait passer
    // des clés locales partagées par cinq formats différents.
    payload._meta.generatorKey = key;
    return payload;
};
